import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import ImportEmployeesModal from './ImportEmployeesModal';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const EmployesTab = () => {
  const { user } = useAuth();
  const [employes, setEmployes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmploye, setSelectedEmploye] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  useEffect(() => {
    fetchEmployes();
  }, []);

  const fetchEmployes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/entreprise/employes');
      setEmployes(response.data);
      console.log('Employés chargés:', response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des employés:', error);
      setEmployes([]);
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-GN', {
      style: 'currency',
      currency: 'GNF',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const handleResetPin = async (employe) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir réinitialiser le PIN de ${employe.prenom} ${employe.nom} ?\n\nUn nouveau PIN sera généré et envoyé par SMS au ${employe.telephone}.`)) {
      return;
    }

    try {
      const response = await api.post(`/api/entreprise/employes/${employe.id}/reset-pin`);
      
      // Afficher le résultat
      const message = response.data.sms_envoye 
        ? `✅ PIN réinitialisé avec succès !\n\n📱 Un SMS a été envoyé à ${employe.prenom} ${employe.nom} (${employe.telephone}) avec son nouveau code PIN.`
        : `✅ PIN réinitialisé !\n\n⚠️ Nouveau PIN: ${response.data.nouveau_pin}\n\nLe SMS n'a pas pu être envoyé. Communiquez ce PIN à l'employé manuellement.`;
      
      alert(message);
      
      // Rafraîchir la liste des employés
      fetchEmployes();
    } catch (error) {
      console.error('Erreur réinitialisation PIN:', error);
      alert(`❌ Erreur lors de la réinitialisation du PIN:\n${error.response?.data?.detail || error.message}`);
    }
  };

  // Filtrer les employés selon le terme de recherche
  const filteredEmployes = employes.filter(emp => 
    emp.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.matricule.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des employés...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* Modal d'import */}
      <ImportEmployeesModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportSuccess={fetchEmployes}
      />
      
      {/* En-tête avec actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex-1">
          <Input
            type="text"
            placeholder="Rechercher un employé..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
        </div>
        <div className="flex space-x-2">
          <Button
            onClick={() => setShowImportModal(true)}
            variant="outline"
            className="flex items-center space-x-2"
          >
            <span>📥</span>
            <span>Importer</span>
          </Button>
          <Button
            onClick={() => setShowAddForm(true)}
            size="sm"
          >
            ➕ Ajouter employé
          </Button>
        </div>
      </div>

      {/* Statistiques rapides */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{employes.length}</div>
              <div className="text-sm text-gray-600">Total employés</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {employes.filter(e => e.statut === 'ACTIF').length}
              </div>
              <div className="text-sm text-gray-600">Actifs</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {employes.filter(e => e.montant_consomme > 0).length}
              </div>
              <div className="text-sm text-gray-600">Ont des avances</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {formatAmount(employes.reduce((sum, e) => sum + e.salaire_net, 0))}
              </div>
              <div className="text-sm text-gray-600">Masse salariale</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Liste des employés avec détails financiers */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Gestion des employés ({filteredEmployes.length})</span>
            <Button variant="outline" size="sm">
              📊 Export
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredEmployes.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">👤</div>
              <p className="text-gray-500">
                {searchTerm ? 'Aucun employé trouvé pour cette recherche' : 'Aucun employé enregistré'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full table-auto">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Employé</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Salaire & Plafond</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Consommé ce mois</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Solde disponible</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Dernier wallet</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Statut</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployes.map((employe) => (
                    <tr key={employe.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div>
                          <div className="font-medium text-gray-900">
                            {employe.prenom} {employe.nom}
                          </div>
                          <div className="text-sm text-gray-500">
                            Mat: {employe.matricule}
                          </div>
                          <div className="text-xs text-gray-400">
                            {employe.telephone}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div>
                          <div className="font-medium text-gray-900">
                            {formatAmount(employe.salaire_net)}
                          </div>
                          <div className="text-sm text-blue-600">
                            Plafond: {employe.plafond_pourcentage}%
                          </div>
                          <div className="text-xs text-gray-500">
                            Max: {formatAmount(employe.montant_max_autorise)}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-center">
                          <div className={`font-medium ${
                            employe.montant_consomme > 0 ? 'text-orange-600' : 'text-gray-400'
                          }`}>
                            {formatAmount(employe.montant_consomme)}
                          </div>
                          {employe.montant_consomme > 0 && (
                            <div className="text-xs text-gray-500">
                              {Math.round((employe.montant_consomme / employe.montant_max_autorise) * 100)}% utilisé
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-center">
                          <div className={`font-medium ${
                            employe.solde_disponible > 0 ? 'text-green-600' : 'text-gray-400'
                          }`}>
                            {formatAmount(employe.solde_disponible)}
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                            <div 
                              className={`h-2 rounded-full ${
                                employe.montant_consomme > 0 ? 'bg-orange-500' : 'bg-gray-300'
                              }`}
                              style={{ 
                                width: `${Math.min(100, (employe.montant_consomme / employe.montant_max_autorise) * 100)}%` 
                              }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-center">
                          {employe.wallet_info ? (
                            <div>
                              <div className="text-sm font-medium text-gray-700">
                                {employe.wallet_info.type}
                              </div>
                              <div className="text-xs text-gray-500">
                                {employe.wallet_info.numero_masque}
                              </div>
                              <div className="text-xs text-blue-600 mt-1">
                                💡 Détails complets réservés à ALO
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">Aucune avance</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          employe.statut === 'ACTIF' 
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {employe.statut}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex space-x-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setSelectedEmploye(employe)}
                            title="Voir détails"
                          >
                            👁️
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleResetPin(employe)}
                            title="Réinitialiser le PIN"
                          >
                            🔑
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de détails employé */}
      {selectedEmploye && (
        <EmployeDetailsModal 
          employe={selectedEmploye}
          onClose={() => setSelectedEmploye(null)}
        />
      )}

      {/* Formulaire d'ajout d'employé */}
      {showAddForm && (
        <AddEmployeForm 
          onClose={() => setShowAddForm(false)}
          onAdd={fetchEmployes}
        />
      )}
    </div>
  );
};

// Modal de détails employé améliorée
const EmployeDetailsModal = ({ employe, onClose }) => {
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-GN', {
      style: 'currency',
      currency: 'GNF',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-96 overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-900">
            Détails financiers - {employe.prenom} {employe.nom}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ✕
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Informations personnelles */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">👤 Informations personnelles</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Matricule:</span>
                  <span className="font-medium">{employe.matricule}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Nom complet:</span>
                  <span className="font-medium">{employe.prenom} {employe.nom}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Téléphone:</span>
                  <span className="font-medium">{employe.telephone}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Statut:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    employe.statut === 'ACTIF' 
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {employe.statut}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Informations financières */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">💰 Informations financières</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Salaire net:</span>
                  <span className="font-bold text-green-600">{formatAmount(employe.salaire_net)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Plafond autorisé:</span>
                  <span className="font-medium text-blue-600">{employe.plafond_pourcentage}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Montant max:</span>
                  <span className="font-medium">{formatAmount(employe.montant_max_autorise)}</span>
                </div>
                <div className="border-t pt-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Consommé ce mois:</span>
                    <span className="font-bold text-orange-600">{formatAmount(employe.montant_consomme)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Solde disponible:</span>
                    <span className="font-bold text-green-600">{formatAmount(employe.solde_disponible)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Informations sur le wallet (masquées) */}
          {employe.wallet_info && (
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="text-sm flex items-center justify-between">
                  📱 Dernier wallet utilisé
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                    Informations limitées
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">
                        Type: {employe.wallet_info.type}
                      </div>
                      <div className="text-sm text-gray-600">
                        Numéro: {employe.wallet_info.numero_masque}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-blue-600 font-medium">
                        🔒 Détails complets réservés à ALO FINANCES
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Date/heure paiement non visible
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        
        <div className="mt-6 flex justify-end space-x-3">
          <Button onClick={onClose} variant="outline">
            Fermer
          </Button>
          <Button>
            Modifier employé
          </Button>
        </div>
      </div>
    </div>
  );
};

// Formulaire d'ajout d'employé simplifié
const AddEmployeForm = ({ onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    matricule: '',
    nom: '',
    prenom: '',
    telephone: '',
    email: '',
    salaire_net: '',
    date_naissance: '',
    date_embauche: new Date().toISOString().split('T')[0],
    contrat_type: 'CDI'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Préparer les données pour l'API
      const employeData = {
        matricule: formData.matricule.trim(),
        nom: formData.nom.trim(),
        prenom: formData.prenom.trim(),
        telephone: formData.telephone.trim(),
        email: formData.email.trim() || null,
        salaire_net: parseFloat(formData.salaire_net),
        wallet: formData.telephone.trim(), // Le téléphone sert aussi de wallet
        date_naissance: formData.date_naissance || null,
        date_embauche: formData.date_embauche,
        contrat_type: formData.contrat_type
      };

      // Appel API pour créer l'employé
      const response = await api.post('/entreprise/employes', employeData);
      
      // Afficher le code PIN généré
      if (response.data && response.data.employe) {
        alert(`✅ Employé créé avec succès!\n\n📱 Code PIN: ${response.data.employe.pin_code}\n\n⚠️ Transmettez ce code PIN à l'employé de manière sécurisée.${response.data.employe.sms_sent ? '\n\n📨 Un SMS a également été envoyé.' : ''}`);
      }
      
      // Succès - rafraîchir la liste et fermer
      onAdd();
      onClose();
    } catch (error) {
      console.error('Erreur lors de l\'ajout de l\'employé:', error);
      setError(
        error.response?.data?.detail || 
        'Erreur lors de l\'ajout de l\'employé. Vérifiez les informations.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Ajouter un employé</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ✕
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Message d'erreur */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">
                <strong>❌ Erreur :</strong> {error}
              </p>
            </div>
          )}

          {/* Matricule */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Matricule *
            </label>
            <Input
              placeholder="Ex: EMP001"
              value={formData.matricule}
              onChange={(e) => setFormData({...formData, matricule: e.target.value})}
              required
            />
          </div>

          {/* Prénom et Nom */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prénom *
              </label>
              <Input
                placeholder="Prénom"
                value={formData.prenom}
                onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom *
              </label>
              <Input
                placeholder="Nom"
                value={formData.nom}
                onChange={(e) => setFormData({...formData, nom: e.target.value})}
                required
              />
            </div>
          </div>

          {/* Téléphone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Téléphone *
            </label>
            <Input
              placeholder="Ex: 621234567"
              value={formData.telephone}
              onChange={(e) => setFormData({...formData, telephone: e.target.value})}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              ℹ️ Ce numéro doit porter un wallet Mobile Money (9 chiffres)
            </p>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email (optionnel)
            </label>
            <Input
              placeholder="email@example.com"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
            />
          </div>

          {/* Salaire */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Salaire net (GNF) *
              </label>
              <Input
                placeholder="Ex: 2500000"
                type="number"
                min="0"
                step="1000"
                value={formData.salaire_net}
                onChange={(e) => setFormData({...formData, salaire_net: e.target.value})}
                required
              />
            </div>
            <div></div>
          </div>

          {/* Date de naissance et Date d'embauche */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date de naissance *
              </label>
              <Input
                type="date"
                value={formData.date_naissance}
                onChange={(e) => setFormData({...formData, date_naissance: e.target.value})}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date d'embauche *
              </label>
              <Input
                type="date"
                value={formData.date_embauche}
                onChange={(e) => setFormData({...formData, date_embauche: e.target.value})}
                required
              />
            </div>
          </div>

          {/* Type de contrat */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type de contrat *
            </label>
            <select
              value={formData.contrat_type}
              onChange={(e) => setFormData({...formData, contrat_type: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="CDI">CDI - Contrat à Durée Indéterminée</option>
              <option value="CDD">CDD - Contrat à Durée Déterminée</option>
              <option value="STAGE">STAGE - Stagiaire</option>
              <option value="INTERIM">INTERIM - Intérimaire</option>
            </select>
          </div>

          {/* Informations importantes */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>ℹ️ Important :</strong> Un code PIN à 4 chiffres sera généré automatiquement pour cet employé. Vous devrez le transmettre de manière sécurisée.
            </p>
          </div>
          
          <div className="flex space-x-3 pt-4">
            <Button type="button" onClick={onClose} variant="outline" className="flex-1" disabled={loading}>
              Annuler
            </Button>
            <Button type="submit" className="flex-1" disabled={loading}>
              {loading ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Ajout en cours...
                </>
              ) : (
                'Ajouter l\'employé'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployesTab;